import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { galleryImages, beforeAfterSlides } from '../data/gallery';
import GlassCard from '../components/GlassCard';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const GalleryPage = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('gallery');

  const content = {
    title: isAr ? 'معرض أعمالنا' : 'Our Work Gallery',
    subtitle: isAr
      ? 'تصفح مجموعة من أعمالنا المتميزة في تلميع العناية بالسيارات في الكويت'
      : 'Browse through our portfolio of premium car detailing work in Kuwait',
    modes: {
      gallery: isAr ? 'المعرض' : 'Gallery',
      'before-after': isAr ? 'قبل وبعد' : 'Before & After',
    },
    categories: {
      all: isAr ? 'الكل' : 'All',
      exterior: isAr ? 'خارجي' : 'Exterior',
      interior: isAr ? 'داخلي' : 'Interior',
      engine: isAr ? 'المحرك' : 'Engine',
      coating: isAr ? 'حماية' : 'Coating',
      polish: isAr ? 'تلميع' : 'Polish',
      full: isAr ? 'كامل' : 'Full',
    },
    baLabels: {
      before: isAr ? 'قبل الخدمة' : 'Before Service',
      after: isAr ? 'بعد الخدمة' : 'After Service',
    },
    stats: [
      { value: '5,000+', label: isAr ? 'سيارة ملمعة' : 'Cars Detailed' },
      { value: '15+', label: isAr ? 'نوع خدمة' : 'Service Types' },
      { value: '100%', label: isAr ? 'نسبة الرضا' : 'Satisfaction Rate' },
      { value: '45min', label: isAr ? 'متوسط الوقت' : 'Avg. Service Time' },
    ],
  };

  const filteredImages =
    activeCategory === 'all'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => setSelectedImage(null);

  const nextImage = () => {
    const nextIdx = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIdx]);
    setCurrentIndex(nextIdx);
  };

  const prevImage = () => {
    const prevIdx =
      (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[prevIdx]);
    setCurrentIndex(prevIdx);
  };

  return (
    <div className='pt-32' dir={isAr ? 'rtl' : 'ltr'}>
      <div className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className='text-center mb-12'
          >
            <h1 className='text-5xl md:text-7xl font-black mb-6 text-gradient'>
              {content.title}
            </h1>
            <p className='text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed'>
              {content.subtitle}
            </p>
          </motion.div>

          {/* View Mode Toggle & Filters */}
          <div
            className={`flex flex-col md:flex-row justify-between items-center gap-6 mb-12`}
          >
            <div className='inline-flex p-1.5 glass-card rounded-full shadow-lg border border-white/10'>
              {['gallery', 'before-after'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
                    viewMode === mode
                      ? 'premium-gradient text-white shadow-md scale-105'
                      : 'text-gray-500 dark:text-gray-400 hover:text-royal-purple-600'
                  }`}
                >
                  {content.modes[mode]}
                </button>
              ))}
            </div>

            {viewMode === 'gallery' && (
              <div
                className={`flex items-center gap-3 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto scrollbar-hide`}
              >
                <Filter className='h-5 w-5 text-royal-purple-500 flex-shrink-0' />
                <div className='flex gap-2'>
                  {Object.keys(content.categories).map((key) => (
                    <button
                      key={key}
                      onClick={() => setActiveCategory(key)}
                      className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-300 ${
                        activeCategory === key
                          ? 'premium-gradient text-white shadow-md'
                          : 'glass-card border border-white/5 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {content.categories[key]}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Gallery View */}
          {viewMode === 'gallery' ? (
            <>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {filteredImages.map((image, index) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className='relative group cursor-pointer'
                    onClick={() => openLightbox(image, index)}
                  >
                    <GlassCard className='p-2 border border-white/10 shadow-xl overflow-hidden'>
                      <div className='aspect-square overflow-hidden rounded-2xl'>
                        <img
                          src={image.src}
                          alt={isAr ? image.altAr : image.alt}
                          className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-700'
                        />
                      </div>
                    </GlassCard>
                    <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-3xl flex items-end p-6'>
                      <div
                        className={`transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 ${isAr ? 'text-right' : 'text-left'}`}
                      >
                        <h4 className='font-black text-white text-lg'>
                          {isAr ? image.titleAr || image.title : image.title}
                        </h4>
                        <p className='text-sm text-gray-200 opacity-90'>
                          {isAr
                            ? image.descriptionAr || image.description
                            : image.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Lightbox */}
              <AnimatePresence>
                {selectedImage && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md'
                    onClick={closeLightbox}
                  >
                    <div
                      className='relative max-w-5xl w-full'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={closeLightbox}
                        className={`absolute -top-12 ${isAr ? 'left-0' : 'right-0'} p-2 text-white hover:text-royal-purple-400`}
                      >
                        <X className='h-8 w-8' />
                      </button>

                      <button
                        onClick={isAr ? nextImage : prevImage}
                        className={`absolute -left-4 md:-left-16 top-1/2 -translate-y-1/2 p-3 text-white hover:scale-125 transition-transform`}
                      >
                        <ChevronLeft
                          className={`h-10 w-10 ${isAr ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <button
                        onClick={isAr ? prevImage : nextImage}
                        className={`absolute -right-4 md:-right-16 top-1/2 -translate-y-1/2 p-3 text-white hover:scale-125 transition-transform`}
                      >
                        <ChevronRight
                          className={`h-10 w-10 ${isAr ? 'rotate-180' : ''}`}
                        />
                      </button>

                      <img
                        src={selectedImage.src}
                        alt={selectedImage.alt}
                        className='w-full h-auto max-h-[75vh] object-contain rounded-2xl shadow-2xl border border-white/10'
                      />

                      <div className='text-center mt-8'>
                        <h3 className='text-3xl font-black text-white mb-2'>
                          {isAr
                            ? selectedImage.titleAr || selectedImage.title
                            : selectedImage.title}
                        </h3>
                        <p className='text-gray-400 text-lg'>
                          {isAr
                            ? selectedImage.descriptionAr ||
                              selectedImage.description
                            : selectedImage.description}
                        </p>
                        <p className='text-royal-purple-400 mt-4 font-mono text-sm tracking-widest'>
                          {currentIndex + 1} / {filteredImages.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            /* Before/After View */
            <div className='space-y-16'>
              {beforeAfterSlides.map((slide, index) => (
                <motion.div
                  key={slide.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <GlassCard
                    className='p-8 md:p-12 border border-white/10 shadow-2xl'
                    lang={lang}
                  >
                    <div className='text-center mb-10'>
                      <h3 className='text-3xl font-black mb-3 text-gradient'>
                        {isAr ? slide.titleAr || slide.title : slide.title}
                      </h3>
                      <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-medium'>
                        {isAr
                          ? slide.descriptionAr || slide.description
                          : slide.description}
                      </p>
                    </div>
                    <BeforeAfterSlider
                      lang={lang}
                      beforeImage={slide.before}
                      afterImage={slide.after}
                      beforeLabel={content.baLabels.before}
                      afterLabel={content.baLabels.after}
                    />
                  </GlassCard>
                </motion.div>
              ))}
            </div>
          )}

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='mt-24'
          >
            <GlassCard className='p-10 border border-white/10 shadow-2xl bg-royal-purple-500/5'>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-8 text-center'>
                {content.stats.map((stat, index) => (
                  <div key={index} className='space-y-1'>
                    <div className='text-4xl font-black text-gradient'>
                      {stat.value}
                    </div>
                    <div className='text-xs font-bold text-gray-500 uppercase tracking-widest'>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
